import React from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import { Link } from 'react-router-dom';
import BackgroundImage from '../../BackgroundImage';
import HorizontalScroll from '../../HorizontalScroll';
import LazyLoad from '../../LazyLoad';
import RoundButton from '../../RoundButton';
import readingTime from '../../../utils/readingTime';
import Truncate from '../../Truncate';
import {
  FeaturedReviewsBackground,
  FeaturedReviewsBackgroundWebp,
} from '../../../assets/landing';
import PlaceholderJpg from '../../../assets/defaultProfileImage.jpg';
import appendUTMParams from '../../../utils/utmParams';

const StyledWrapper = styled(BackgroundImage)`
  background-color: #f8f8f8;
  padding: 50px 0 100px;
  background-size: cover;
  background-position: center;
  background-repeat: repeat-x;
`;

const StyledContent = styled(Container)`
  width: 100%;
  .ArticlesList__image {
    height: 221px;
    overflow: hidden;
    position: relative;
    width: 100%;
    border-radius: 8px;
  }

  .ArticlesList__image img {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`;

const StyledHeading = styled.h3`
  font-size: 26px;
  margin-bottom: 40px;
  font-weight: bold;
  &.ArticlesList__heading--hasSubHeading {
    margin-bottom: 10px;
  }

  @media (max-width: 767px) {
    line-height: 1.2em;
  }
`;

const StyledSubHeading = styled.h4`
  color: #757575;
  font-size: 20px;
  margin-top: 0;
  font-weight: 500;
  margin-bottom: 45px;

  @media (max-width: 767px) {
    font-size: 18px;
  }
`;

const StyledLayoutContainer = styled(Container)`
  max-width: 1300px;
  margin: 0 auto;
  padding-right: 30px;
  padding-left: 30px;
  @media (max-width: 767px) {
    padding-left: 25px;
    padding-right: 25px;
  }
`;

const StyledList = styled(Container)`
  margin-left: auto;
  margin-right: auto;
  .HorizontalScroll__root .scroll-menu-arrow {
    top: 92px;
  }
`;

const StyledTitle = styled(Container)`
  color: ${props => props.theme.text.normal};
  width: 100%;
  padding: 14px 0;
  z-index: 1;
  position: relative;
  font-size: 18px;
  max-width: 100%;
  min-height: 70px;
  font-family: 'Circular';
  font-weight: bold;
  line-height: 1.2;
`;

const StyledCol = styled(Container)`
  max-width: 280px;
  min-width: 280px;
  cursor: pointer;
  margin-right: 22px;
`;

const StyledColInner = styled(Container)`
  width: 100%;
  height: auto;
  display: flex;
  position: relative;
  align-items: flex-start;
  flex-direction: column;
  background-size: cover;
  background-position: center;

  .ArticlesList__hover {
    opacity: 0;
    top: 50%;
    left: 50%;
    width: 200px;
    height: 44px;
    position: absolute;
    transform: translate(-50%, -50%);
    transition: all 0.3s ease;
  }
  &:hover .ArticlesList__hover {
    opacity: 1;
  }
`;

const StyledLink = styled(Link)`
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
  z-index: 2;
  overflow: hidden;
  position: absolute;
  text-indent: -999999px;
`;

const StyledDate = styled.p`
  font-size: 14px;
  line-height: 1.21;
  color: #757575;
  margin-bottom: 0;
  margin-top: 0;
`;

const ArticlesList = ({ title, subHeading, articles, target = '' }) => {
  return (
    <StyledWrapper
      className="ArticlesList__wrapper"
      src={FeaturedReviewsBackgroundWebp}
      fallback={FeaturedReviewsBackground}
    >
      <StyledLayoutContainer>
        <StyledContent className="ArticlesList__content">
          <div className="ArticlesList">
            <StyledHeading
              className={classNames('ArticlesList__heading', {
                'ArticlesList__heading--hasSubHeading': subHeading,
              })}
            >
              {title}
            </StyledHeading>
            {!!subHeading && (
              <StyledSubHeading className="ArticlesList__subHeading">
                {subHeading}
              </StyledSubHeading>
            )}
          </div>
          <StyledList className="ArticlesList__list">
            <HorizontalScroll
              lockScroll
              className="ArticlesList__row"
              arrows
              data={articles.map(
                (
                  {
                    article: {
                      articleTitle,
                      urlSlug,
                      featureImageUrl,
                      wordCount,
                    },
                  },
                  key
                ) => (
                  <StyledCol key={key.toString()} className="ArticlesList__col">
                    <StyledColInner className="ArticlesList__colInner">
                      <StyledLink
                        rel="noopener noreferrer"
                        className="ArticlesList__link"
                        to={appendUTMParams(urlSlug)}
                        target={target}
                      >
                        {articleTitle}
                      </StyledLink>
                      <LazyLoad className="ArticlesList__image">
                        <img
                          src={featureImageUrl || PlaceholderJpg}
                          alt={articleTitle}
                          onError={e => {
                            e.target.onerror = null;
                            e.target.src = PlaceholderJpg;
                          }}
                        />
                        <RoundButton
                          className="ArticlesList__hover"
                          tag="div"
                          theme="outlineBlue"
                        >
                          Take me to this article
                        </RoundButton>
                      </LazyLoad>
                      <StyledTitle className="ArticlesList__title">
                        <Truncate lines={2}>
                          <div>{articleTitle}</div>
                        </Truncate>
                      </StyledTitle>
                      <Container display="flex" className="ArticlesList__meta">
                        <div>
                          <StyledDate className="ArticlesList__date">
                            {readingTime(wordCount)}
                          </StyledDate>
                        </div>
                      </Container>
                    </StyledColInner>
                  </StyledCol>
                )
              )}
            />
          </StyledList>
        </StyledContent>
      </StyledLayoutContainer>
    </StyledWrapper>
  );
};

export default ArticlesList;
